import {
	type BillCalculation,
	type BillingSettings,
	billFinancialYear,
	billKindFor,
	billPrefixSchema,
	calculateBill,
	formatBillNo,
	fssaiNoSchema,
	type UpdateBillingSettingsInput,
	upiVpaSchema,
} from "@luckymenu/billing-core";
import {
	type BillKind,
	checkedGstinSchema,
	type GstMode,
	gstinSchema,
	parseRupees,
} from "@luckymenu/core";

/**
 * More > Billing settings (MG-04, P4-T23): the form's fields as typed, the
 * checks the server makes (so the form says what is wrong before saving),
 * the changed fields to send, and a sample bill drawn from the form as it
 * is now. Tax rates are tax classes, edited with the menu.
 */

export type BillingSettingsDraft = {
	gstMode: GstMode;
	gstin: string;
	fssaiNo: string;
	pricesIncludeTax: boolean;
	/** Percent as typed: "5", "7.5"; empty for none. */
	serviceCharge: string;
	roundOff: boolean;
	billPrefix: string;
	billHeader: string;
	billFooter: string;
	upiVpa: string;
	upiPayeeName: string;
	/** Percent as typed. */
	cashierDiscountLimit: string;
};

export type SettingsField = keyof BillingSettingsDraft;

/** Translated from `billing:settings.problems.<problem>`. */
export type SettingsProblem =
	| "required"
	| "gstinFormat"
	| "gstinCheck"
	| "fssai"
	| "upi"
	| "prefix"
	| "percent"
	| "tooLong"
	| "invalid";

export type SettingsErrors = Partial<Record<SettingsField, SettingsProblem>>;

/** Basis points to the percent a field shows: 500 → "5", 750 → "7.5", 0 → "". */
export function bpToPercent(bp: number, emptyForZero = false): string {
	if (bp === 0 && emptyForZero) return "";
	const whole = Math.trunc(bp / 100);
	const rest = bp % 100;
	return rest === 0 ? String(whole) : `${whole}.${String(rest).padStart(2, "0").replace(/0$/, "")}`;
}

/** A typed percent to basis points ("7.5" → 750); empty is 0. Null when not 0 to 100. */
export function percentToBp(text: string): number | null {
	if (text.trim() === "") return 0;
	const hundredths = parseRupees(text.replace(/%\s*$/, ""));
	if (hundredths === null || hundredths > 10000) return null;
	return hundredths;
}

export function settingsToDraft(s: BillingSettings): BillingSettingsDraft {
	return {
		gstMode: s.gstMode,
		gstin: s.gstin ?? "",
		fssaiNo: s.fssaiNo ?? "",
		pricesIncludeTax: s.pricesIncludeTax,
		serviceCharge: bpToPercent(s.serviceChargeBp, true),
		roundOff: s.roundOff,
		billPrefix: s.billPrefix ?? "",
		billHeader: s.billHeader ?? "",
		billFooter: s.billFooter ?? "",
		upiVpa: s.upiVpa ?? "",
		upiPayeeName: s.upiPayeeName ?? "",
		cashierDiscountLimit: bpToPercent(s.cashierDiscountLimitBp),
	};
}

/** The settings the form describes, or what is wrong with each field. */
export type CheckedSettings = {
	gstMode: GstMode;
	gstin: string | null;
	fssaiNo: string | null;
	pricesIncludeTax: boolean;
	serviceChargeBp: number;
	roundOff: boolean;
	billPrefix: string | null;
	billHeader: string | null;
	billFooter: string | null;
	upiVpa: string | null;
	upiPayeeName: string | null;
	cashierDiscountLimitBp: number;
};

const text = (v: string) => (v.trim() === "" ? null : v.trim());

export function checkSettings(
	draft: BillingSettingsDraft,
): { ok: true; values: CheckedSettings } | { ok: false; errors: SettingsErrors } {
	const errors: SettingsErrors = {};
	let gstin = text(draft.gstin);
	if (gstin) {
		const format = gstinSchema.safeParse(gstin);
		if (!format.success) errors.gstin = "gstinFormat";
		else if (!checkedGstinSchema.safeParse(gstin).success) errors.gstin = "gstinCheck";
		else gstin = format.data;
	} else if (draft.gstMode !== "none") errors.gstin = "required";
	const fssaiNo = text(draft.fssaiNo);
	if (fssaiNo && !fssaiNoSchema.safeParse(fssaiNo).success) errors.fssaiNo = "fssai";
	const upiVpa = text(draft.upiVpa);
	if (upiVpa && !upiVpaSchema.safeParse(upiVpa).success) errors.upiVpa = "upi";
	const billPrefix = text(draft.billPrefix);
	if (billPrefix && !billPrefixSchema.safeParse(billPrefix).success) errors.billPrefix = "prefix";
	const serviceChargeBp = percentToBp(draft.serviceCharge);
	if (serviceChargeBp === null) errors.serviceCharge = "percent";
	const cashierDiscountLimitBp = percentToBp(draft.cashierDiscountLimit);
	if (cashierDiscountLimitBp === null) errors.cashierDiscountLimit = "percent";
	const long = (field: SettingsField, max: number) => {
		const value = draft[field];
		if (typeof value === "string" && value.trim().length > max) errors[field] = "tooLong";
	};
	long("billHeader", 200);
	long("billFooter", 200);
	long("upiPayeeName", 50);
	if (Object.keys(errors).length > 0 || serviceChargeBp === null || cashierDiscountLimitBp === null)
		return { ok: false, errors };
	return {
		ok: true,
		values: {
			gstMode: draft.gstMode,
			gstin,
			fssaiNo,
			pricesIncludeTax: draft.pricesIncludeTax,
			serviceChargeBp,
			roundOff: draft.roundOff,
			billPrefix,
			billHeader: text(draft.billHeader),
			billFooter: text(draft.billFooter),
			upiVpa,
			upiPayeeName: text(draft.upiPayeeName),
			cashierDiscountLimitBp,
		},
	};
}

/**
 * Only the fields that changed, as `updateSettings` takes them: a cleared
 * optional field is sent as empty text. Empty when nothing changed.
 */
export function settingsChanges(
	values: CheckedSettings,
	saved: BillingSettings,
): UpdateBillingSettingsInput {
	const changes: Record<string, unknown> = {};
	for (const key of Object.keys(values) as (keyof CheckedSettings)[]) {
		const value = values[key];
		const before = saved[key];
		if (value === before) continue;
		changes[key] = value === null ? "" : value;
	}
	return changes as UpdateBillingSettingsInput;
}

/** A server `VALIDATION` field (api.md: the field path) to the form's field. */
export function settingsFieldOf(field: string | undefined): SettingsField | null {
	if (!field) return null;
	const key = field.split(".")[0];
	if (key === "serviceChargeBp") return "serviceCharge";
	if (key === "cashierDiscountLimitBp") return "cashierDiscountLimit";
	const fields: SettingsField[] = [
		"gstMode",
		"gstin",
		"fssaiNo",
		"billPrefix",
		"billHeader",
		"billFooter",
		"upiVpa",
		"upiPayeeName",
	];
	return fields.find((f) => f === key) ?? null;
}

/** A made-up order for the preview. Names come from the app, translated. */
export const SAMPLE_LINES = [
	{ unitPricePaise: 24_000, quantity: 2 },
	{ unitPricePaise: 4_500, quantity: 3 },
	{ unitPricePaise: 3_000, quantity: 2 },
] as const;

export type SampleBill = {
	kind: BillKind;
	billNo: string;
	calculation: BillCalculation;
	gstin: string | null;
	fssaiNo: string | null;
	header: string | null;
	footer: string | null;
	/** Who UPI apps show as the payee: the payee name, else the restaurant. Null without a UPI ID. */
	upiPayee: string | null;
};

/**
 * The sample bill for the live preview: the made-up lines at the default
 * tax rate, through billing-core with the settings in the form, numbered as
 * the first bill of this financial year. Fields with a problem fall back to
 * nothing, so the preview never breaks while staff type.
 */
export function sampleBill(
	draft: BillingSettingsDraft,
	options: {
		names: readonly [string, string, string];
		restaurantName: string;
		defaultTaxRateBp: number;
		now?: Date;
		timeZone?: string;
	},
): SampleBill {
	const prefix = billPrefixSchema.safeParse(draft.billPrefix.trim());
	const fy = billFinancialYear(options.now ?? new Date(), options.timeZone);
	let billNo: string;
	try {
		billNo = formatBillNo(prefix.success ? prefix.data : "", fy, 1);
	} catch {
		billNo = formatBillNo("", fy, 1);
	}
	const calculation = calculateBill({
		gstMode: draft.gstMode,
		pricesIncludeTax: draft.pricesIncludeTax,
		roundOff: draft.roundOff,
		serviceChargeBp: percentToBp(draft.serviceCharge) ?? 0,
		defaultTaxRateBp: options.defaultTaxRateBp,
		lines: SAMPLE_LINES.map((line, i) => ({
			id: `sample-${i}`,
			name: options.names[i] ?? "",
			variantName: null,
			modifiers: [],
			unitPricePaise: line.unitPricePaise,
			modifiersTotalPaise: 0,
			quantity: line.quantity,
			taxRateBp: options.defaultTaxRateBp,
			discount: null,
		})),
		billDiscount: null,
	});
	const vpa = text(draft.upiVpa);
	return {
		kind: billKindFor(draft.gstMode),
		billNo,
		calculation,
		gstin: draft.gstMode === "none" ? null : (text(draft.gstin)?.toUpperCase() ?? null),
		fssaiNo: text(draft.fssaiNo),
		header: text(draft.billHeader),
		footer: text(draft.billFooter),
		upiPayee: vpa ? (text(draft.upiPayeeName) ?? options.restaurantName) : null,
	};
}
